import { defineComponent as _defineComponent } from 'vue'
import { defineMessages } from '@/lib/i18n';
import { i18n as i18nModule } from '@/plugins/vue-i18n';
import { computed, onMounted, PropType, ref, watch, reactive } from 'vue';
import { Department } from '@/interfaces/model/general/department';
import { StatusId, AttachmentLimitId, CountryCode } from '@/lib/enum';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { useTagComposable } from '@/composables/useTag';
import {
  formatToDate,
  formatToDateTime,
  now,
  formatDatePickerDay,
} from '@/utils/datetime';
import { FormItem } from '@/interfaces/lib/model';
import { SdsProduct } from '@/interfaces/model/general/sds';
import { Tag, TagResponse } from '@/interfaces/model/general/tag';

import { Attachment } from '@/interfaces/model/general/attachment';
import { User } from '@/interfaces/vuex/user';
import { usePermissionComposable } from '@/composables/usePermission';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { patternExtractFileExtension } from '@/lib/regex';
import { hasPdfExtension, hasNoPathSeparator } from '@/lib/rule';
import { VForm } from 'vuetify/lib/components';
import { store } from '@/store/index';
import {
  dispatchGetAttachmentGcsSignedUrl,
  dispatchGetCompanySdsAttachmentPdf,
  dispatchGetCompanySdsJson,
} from '@/store/main/actionsMain';
import { commitAddNotification } from '@/store/main/mutations';
import { MAX_FILE_SIZE } from '@/lib/constant';
import { MIME_TYPE } from '@/lib/enum/MimeType';
import UUID from 'uuidjs';

import sharedMessages from '@/lib/shared-messages';
import SelectTags from '@/components/SelectTags.vue';
import TagChip from '@/components/TagChip.vue';
import StateChip from '@/components/StateChip.vue';
import SelectInTextField from '@/components/SelectInTextField.vue';
import TextTruncatedChip from '@/components/TextTruncatedChip.vue';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import { buildDepartmentPathMap } from '@/utils/departmentTree';
import { splitTextWithLinks } from '@/utils/splitTextWithLinks';
import { readAssertedCompany } from '@/store/main/getters';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSectionDefault',
  props: {
  productFormItems: {
    type: Array as PropType<Array<FormItem>>,
    value: () => [],
    required: true,
  },
  statusList: {
    type: Array as PropType<Array<FormItem>>,
    default: () => [],
  },
  statusId: {
    type: Number,
    default: null,
  },
  uuid: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  createdAt: {
    type: String,
    default: null,
  },
  lastUpdatedAt: {
    type: String,
    default: null,
  },
  uploadUser: {
    type: Object as PropType<User | null>,
    default: null,
  },
  assigneeUsers: {
    type: Array as PropType<User[]>,
    default: () => [],
  },
  companyUsers: {
    type: Array as PropType<User[]>,
    default: () => [],
  },
  lastCrossCheckEndedAt: {
    type: String,
    default: null,
  },
  publishedAt: {
    type: String,
    default: null,
  },
  revisedAt: {
    type: String,
    default: null,
  },
  assessedAt: {
    type: String as PropType<string | null>,
    required: false,
    default: null,
  },
  note: {
    type: String,
    default: '',
  },
  adminNote: {
    type: String,
    default: '',
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
  productCodeList: {
    type: Array as PropType<Array<string>>,
    default: () => [],
  },
  fileTags: {
    type: Array as PropType<Array<TagResponse>>,
    required: true,
    default: () => [],
  },
  attachments: {
    type: Array as PropType<Array<Attachment>>,
    default: () => [],
  },
  companyDepartments: {
    type: Array as PropType<Array<Department>>,
    required: true,
  },
  departments: {
    type: Array as PropType<Array<Department>>,
    required: true,
  },
  canExportCreateSimpleCountV3: {
    type: Number,
    default: 0,
    required: false,
  },
  canExportCreateSimpleCount: {
    type: Number,
    default: 0,
    required: false,
  },
  statusOptions: {
    type: Array as PropType<{ value: string; state: string }[]>,
    required: false,
    default: () => [],
  },
},
  emits: ["update:isValid", "update:name", "update:publishedAt", "update:revisedAt", "update:assessedAt", "update:statusId", "update:note", "update:products", "update:productIndex", "update:productFormItems", "update:productCodeList", "update:fileTags", "update:assigneeUsers", "update:departments", "update:attachments", "exportCreateSimple", "exportCreateSimpleV3", "exportCreateSimpleV3_2", "update:isUploading"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:name', data: string): void;
  (e: 'update:publishedAt', data: string | null): void;
  (e: 'update:revisedAt', data: string | null): void;
  (e: 'update:assessedAt', data: string | null): void;
  (e: 'update:statusId', data: string): void;
  (e: 'update:note', data: string): void;
  (e: 'update:products', data: Array<SdsProduct>): void;
  (e: 'update:productIndex', data: number): void;
  (e: 'update:productFormItems', data: Array<FormItem>): void;
  (e: 'update:productCodeList', data: string[]): void;
  (e: 'update:fileTags', data: TagResponse[]): void;
  (e: 'update:assigneeUsers', data: User[]): void;
  (e: 'update:departments', data: Department[]): void;
  (e: 'update:attachments', data: Array<Attachment>): void;
  (e: 'exportCreateSimple', data: string): void;
  (e: 'exportCreateSimpleV3', data: string): void;
  (e: 'exportCreateSimpleV3_2', data: string): void;
  (e: 'update:isUploading', data: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const company = reactive({ ...readAssertedCompany(store) });

const t = i18nModule.tc.bind(i18nModule);

const m = defineMessages({
  lastUpdatedAt: '最終更新日時',
  analysisCompletionDate: 'データ化完了日時',
  noAssignees: '担当ユーザーはいません。',
  noDepartment: '部署はありません。',
  noTags: 'タグはありません。',
  filesCanBeGenerated: '{count}件ファイルを生成できます',
  noFilesCanBeGenerated: 'ファイルを生成できる対象がありません',
  noEditBelowUntilAnalysis: '(データ化完了まで以下の情報は編集できません)',
  pleaseEnter: '入力してください',
  pleasePressEnter: '入力後はEnterを押して確定してください',
  attachmentLimitError:
    '現在ご契約のプランでは、SDSに添付できるのは{count}件のファイルです。添付できるファイル数を増やしたい場合は、ケミカンメニューバー右上の「お問い合わせ」から追加オプションの購入をリクエストしてください。',
  uploadFileSizeLimitError: 'ファイルサイズが10MBの制限を超えています',
  uploadFileTypeError: 'ファイルタイプが無効です。',
  attachmentUploadFailed:
    '添付ファイルのアップロードに失敗しました。時間をおいて再度お試しください',
  fileUploadPrompt: 'ここにファイルをドロップまたはファイルを選択',
  attachmentCaution:
    '※添付ファイルには、SDSに付随する関連資料（CREATE-SIMPLE・社内管理資料 など）を保管できます。\n新しいSDSは、ここに添付しても登録されません。{0}からご登録ください。',
  attachmentCautionLink: '「SDS登録」',
  attachments: '添付ファイル',
  downloadInformationText: '各ファイルをクリックするとダウンロードいただけます',
  confirmDeleteAttachment: '添付ファイルを削除しますか？',
  confirmDeleteAttachmentMessage:
    '削除するとファイルが失われます。\n削除して問題ない場合は[削除]を押してください。',
});



const MIN_FOLDED_ATTACHMENTS_COUNT = 10;

const {
  isOwnDepartment,
  companyDepartments: allCompanyDepartments,
  departmentTree,
  editableDepartmentTree,
  getCompanyDepartmentTree,
} = useDepartmentComposable();
const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

// Editors without SDS_EDIT_OTHER_DEPARTMENT may only assign departments
// within their own edit scope (self + descendants), not the whole company
// tree - same scoping as the Upload page and Bulk Edit dialog.
const selectableDepartmentTree = computed(() =>
  hasPermission(P.SDS_EDIT_OTHER_DEPARTMENT)
    ? departmentTree.value
    : editableDepartmentTree.value
);

/**
 * 親が違えば同名の部署を作れる仕様なので、名前だけでは同名の部署を区別できない
 * (3 階層以上では直近の親も同名になりうるため、1 階層上の親名だけでも不十分)。
 * 親の見える範囲は role/所属で絞られる場合があるため、絞り込み前の一覧 (companyDepartments prop ではなく
 * useDepartmentComposable 由来) からフルパスを組む。
 */
const departmentPathByUuid = computed(() =>
  buildDepartmentPathMap(allCompanyDepartments.value)
);
const departmentLabel = (department: Department): string =>
  departmentPathByUuid.value.get(department.uuid) ?? department.name;
const selectedProductIndex = ref<number>(0);
const isValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>(null);
const modalDeleteProduct = ref<boolean>(false);
const selectedTags = ref<TagResponse[]>([...props.fileTags]);
const selectableTags = ref<Tag[]>([]);
const modalAssessedAt = ref<boolean>(false);
const isAttachmentLimitErrorVisible = ref<boolean>(false);
const files = ref<File[]>([]);
const localAttachments = ref<Attachment[]>([]);
const isAttachmentsFolded = ref<boolean>(true);
const productCode = ref<string>('');
const localProductCodes = ref<string[]>([]);
const isUploading = ref<boolean>(false);

const clearProductCode = () => {
  productCode.value = '';
};

const updateProductCodeList = () => {
  if (productCode.value.trim()) {
    const newProductCode: string = productCode.value.trim();
    const updatedProductCodeList = [...localProductCodes.value, newProductCode];
    productCode.value = '';
    localProductCodes.value = updatedProductCodeList;
    emits('update:productCodeList', updatedProductCodeList);
  }
};

const removeProductCode = (product_code: string) => {
  const updatedProductCodeList = localProductCodes.value.filter(
    (item) => item !== product_code
  );
  localProductCodes.value = updatedProductCodeList;
  emits('update:productCodeList', updatedProductCodeList);
};

const closeAssessedAtModal = () => {
  modalAssessedAt.value = false;
};



const { tags, getCompanyTag } = useTagComposable();
const { showConfirm } = useConfirmDialog();

const closeDeleteProductModal = (): void => {
  modalDeleteProduct.value = false;
};

const handleDeleteProduct = (): void => {
  deleteProduct();
  closeDeleteProductModal();
};

const toggleToFoldAttachments = (): void => {
  isAttachmentsFolded.value = !isAttachmentsFolded.value;
};

const canFoldItems = (): boolean => {
  return props.attachments.length > MIN_FOLDED_ATTACHMENTS_COUNT;
};

const calcName = computed({
  get: (): string => {
    return props.name;
  },
  set: (e: string): void => {
    const fileNameWithoutExtension = e.replace(patternExtractFileExtension, '');

    emits('update:name', `${fileNameWithoutExtension}.pdf`);
  },
});

const attachmentLimit = computed((): number => {
  return AttachmentLimitId.fromAttachmentLimitId(
    store.state.main.company.attachment_limit_id
  ).value;
});

const updateIsValid = (e: boolean) => {
  emits('update:isValid', e);
};

const updateNote = (e: string) => {
  emits('update:note', e);
};

const updateAssessedAt = (e: string | null) => {
  emits('update:assessedAt', e);
};

const deleteProduct = (): void => {
  const updatedData = [...props.products];
  updatedData[selectedProductIndex.value].deleted_at = now().toString();
  updatedData[selectedProductIndex.value].sds_section2_label.deleted_at =
    now().toString();
  for (const x of updatedData[selectedProductIndex.value]
    .sds_section1_manufacturers) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[selectedProductIndex.value]
    .sds_section1_importers) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[selectedProductIndex.value].sds_section2_list) {
    x.deleted_at = now().toString();
    for (const y of x.sds_section2_hazard_list) {
      y.deleted_at = now().toString();
    }
  }
  updatedData[selectedProductIndex.value].sds_section3.deleted_at =
    now().toString();
  for (const x of updatedData[selectedProductIndex.value].sds_section3
    .material_list) {
    x.deleted_at = now().toString();
    for (const y of x.cas_list) {
      y.deleted_at = now().toString();
    }
  }
  for (const x of updatedData[selectedProductIndex.value]
    .sds_section8_exposure_control_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
    }
  }
  for (const x of updatedData[selectedProductIndex.value].sds_section9_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[selectedProductIndex.value]
    .sds_section9_flash_point_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[selectedProductIndex.value]
    .sds_section9_boiling_point_list) {
    x.deleted_at = now().toString();
  }
  for (const x of updatedData[selectedProductIndex.value].sds_section15_list) {
    x.deleted_at = now().toString();
    for (const y of x.material_list) {
      y.deleted_at = now().toString();
      for (const z of y.cas_list) {
        z.deleted_at = now().toString();
      }
    }
  }
  emits('update:products', updatedData);

  const formItems = updatedData.map((x, idx) => {
    return {
      text: x.name,
      value: idx,
      disabled: !!x.deleted_at,
    };
  });

  emits('update:productFormItems', formItems);
};

const noteParts = computed(() => {
  return splitTextWithLinks(props.note);
});

const updateSelectDepartments = (updateValues: string[]) => {
  const formattedSelectDepartment = updateValues
    .map((d) => props.companyDepartments.find((cd) => cd.uuid === d))
    .filter((d) => !!d);
  emits('update:departments', formattedSelectDepartment);
};

// 未有効化ユーザーは選択不可。ただし、保存済みの担当者は表示と保持のため残す
const assigneeUserOptions = computed(() =>
  props.companyUsers
    .filter(
      (u) => u.is_active || props.assigneeUsers.some((a) => a.uuid === u.uuid)
    )
    .map((u) => ({
      value: u.uuid,
      title: `${u.last_name} ${u.middle_name} ${u.first_name}`,
    }))
);

const currentSettingsTags = computed(() =>
  tags.value.filter((tag) =>
    props.fileTags.map((fileTag) => fileTag.id).includes(tag.id)
  )
);

const statusChipClass = (stateValue: string): string => {
  const state = props.statusOptions.find((state) => state.value === stateValue);
  return state ? state.state : '';
};

const exportCreateSimple = (sdsId: string) => {
  emits('exportCreateSimple', sdsId);
};

const exportCreateSimpleV3 = (sdsId: string) => {
  emits('exportCreateSimpleV3', sdsId);
};

const exportCreateSimpleV3_2 = (sdsId: string) => {
  emits('exportCreateSimpleV3_2', sdsId);
};

const fileInputRef = ref(null);

const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  } else {
    console.error('File input is not available');
  }
};

const handleFileUpload = async (e: Event) => {
  // The drawer can render before the active SDS is loaded (the uuid prop
  // defaults to ''); an upload fired in that window would POST to
  // /sdses//attachments/get_signed_url and 404.
  if (!props.uuid) {
    return;
  }

  let tmp: Array<File> = [];

  if (
    !(
      e instanceof DragEvent &&
      e.dataTransfer &&
      e.dataTransfer.files.length
    ) &&
    !(
      e.target &&
      (e.target as HTMLInputElement).files &&
      (e.target as HTMLInputElement).files.length
    )
  ) {
    return;
  }

  if (e instanceof DragEvent && e.dataTransfer && e.dataTransfer.files.length) {
    tmp = Array.from(e.dataTransfer.files);
  }

  if (
    e.target &&
    (e.target as HTMLInputElement).files &&
    (e.target as HTMLInputElement).files.length
  ) {
    tmp = Array.from((e.target as HTMLInputElement).files);
  }

  if (localAttachments.value.length + tmp.length > attachmentLimit.value) {
    isAttachmentLimitErrorVisible.value = true;
    return;
  } else {
    isAttachmentLimitErrorVisible.value = false;
  }

  const validMimeTypes = Object.values(MIME_TYPE);

  for (let i = 0; i < tmp.length; i++) {
    if (tmp[i].size > MAX_FILE_SIZE) {
      commitAddNotification(store, {
        content: `${t(m.uploadFileSizeLimitError)}: ${tmp[i].name}`,
        color: 'error',
      });
      return;
    }

    if (!validMimeTypes.includes(tmp[i].type.toLowerCase() as MIME_TYPE)) {
      commitAddNotification(store, {
        content: `${t(m.uploadFileTypeError)}: ${tmp[i].name}`,
        color: 'error',
      });
      return;
    }
  }

  try {
    if (tmp.length > 0) {
      isUploading.value = true;
      emits('update:isUploading', true);
      files.value = [...files.value, ...tmp];
      const res = await dispatchGetAttachmentGcsSignedUrl(store, {
        companyId: store.state.main.company.uuid,
        sdsId: props.uuid,
        attachments: tmp.map((file) => ({
          uuid: UUID.generate(),
          sds_id: props.uuid,
          name: file.name,
          mime_type: file.type,
          gcs_signed_url: '',
        })),
      });

      // Upload to GCS first, and only persist attachments whose object
      // actually landed. A swallowed PUT failure here previously left an
      // attachment row with no backing GCS object (download 404).
      const uploadedAttachments: Attachment[] = [];
      const failedFiles: File[] = [];
      for (const attachment of res.data) {
        const file = tmp.find((f) => f.name === attachment.name);
        if (!file || !attachment.gcs_signed_url) {
          if (file) failedFiles.push(file);
          continue;
        }
        try {
          const response = await fetch(attachment.gcs_signed_url, {
            method: 'PUT',
            headers: {
              'x-goog-meta-sds_uuid': attachment.sds_id,
              'x-goog-meta-company_id': store.state.main.company.uuid,
              'x-goog-meta-attachment_uuid': attachment.uuid,
            },
            body: file,
          });
          if (!response.ok) {
            throw new Error(`Failed to upload file: ${response.statusText}`);
          }
          uploadedAttachments.push(attachment);
        } catch (error) {
          console.error('Error uploading file to GCS:', error);
          failedFiles.push(file);
        }
      }

      // Persist only the successfully-uploaded attachments.
      if (uploadedAttachments.length > 0) {
        emits('update:attachments', [
          ...localAttachments.value,
          ...uploadedAttachments,
        ]);
      }

      // Drop failed files from the staged list and surface the failure.
      if (failedFiles.length > 0) {
        files.value = files.value.filter((f) => !failedFiles.includes(f));
        commitAddNotification(store, {
          content: `${t(m.attachmentUploadFailed)}: ${failedFiles
            .map((f) => f.name)
            .join(', ')}`,
          color: 'error',
        });
      }

      isUploading.value = false;
      emits('update:isUploading', false);
    }
  } catch (error) {
    console.error(error);
    isUploading.value = false;
    emits('update:isUploading', false);
  }
};

const removeAttachment = async (index: number) => {
  if (index >= 0 && index < localAttachments.value.length) {
    const confirmed = await showConfirm({
      title: t(m.confirmDeleteAttachment),
      message: t(m.confirmDeleteAttachmentMessage).toString(),
      confirmText: t(sharedMessages.delete).toString(),
      cancelText: t(sharedMessages.cancel).toString(),
      confirmColor: 'error',
      icon: 'mdi-delete',
    });

    if (confirmed) {
      localAttachments.value.splice(index, 1);
      emits('update:attachments', localAttachments.value);
    }
  }
};

const downloadFile = async (uuid: string, fileName: string) => {
  if (!hasPermission(P.SDS_DOWNLOAD)) return;

  try {
    const response = await dispatchGetCompanySdsAttachmentPdf(store, {
      companyId: store.state.main.company.uuid,
      sdsUuid: props.uuid,
      attachmentUuid: uuid,
    });

    if (response.data) {
      const base64Data = response.data.data;
      const blob = await fetch(
        `data:application/octet-stream;base64,${base64Data}`
      ).then((res) => res.blob());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error:', error);
  }
};

const handleChangeAssigneeUsers = (uuids: string[]) => {
  const users = uuids
    .map((id) => props.companyUsers.find((user) => user.uuid === id))
    .filter((u) => !!u);
  emits('update:assigneeUsers', users);
};

const calcCountOfDisplayedAttachments = (
  isFolded: boolean,
  featureDisabled: boolean
): number => {
  const { attachments } = props;

  return isFolded && !featureDisabled
    ? Math.min(attachments.length, MIN_FOLDED_ATTACHMENTS_COUNT)
    : attachments.length;
};

watch(
  () => props.fileTags,
  () => (selectedTags.value = [...props.fileTags])
);

watch(
  () => props.productCodeList,
  (newProductCodes) => {
    localProductCodes.value = [...newProductCodes];
  },
  { immediate: true }
);

watch(
  () => props.attachments,
  (newAttachments) => {
    localAttachments.value = [...newAttachments];
  },
  { immediate: true }
);

onMounted(async () => {
  if (form.value) {
    form.value.validate();
  }
  await getCompanyTag();
  await getCompanyDepartmentTree();
  selectableTags.value = tags;
});
const updatableValuesNum = StatusId.updatableValues();
const canEdit = computed((): boolean => {
  return updatableValuesNum.includes(props.statusId);
});

const isDownloadingJson = ref<boolean>(false);

const downloadJson = async (): Promise<void> => {
  if (!hasPermission(P.SDS_DOWNLOAD)) return;
  if (isDownloadingJson.value) return;
  isDownloadingJson.value = true;
  try {
    const response = await dispatchGetCompanySdsJson(store, {
      companyId: company.uuid,
      sdsUuid: props.uuid,
    });
    if (!response?.data) return;

    // 交換フォーマットは1製品=1ドキュメントのため、複数製品SDSはZIPで返る
    const isZip = response.headers['content-type']?.includes('application/zip');
    const blob = new Blob([response.data], {
      type: isZip ? 'application/zip' : 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${props.name.replace(patternExtractFileExtension, '')}.${
      isZip ? 'zip' : 'json'
    }`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error:', error);
  } finally {
    isDownloadingJson.value = false;
  }
};

const canShowCreateSimple = computed((): boolean => {
  return (
    company.country_code === CountryCode.JP.value &&
    props.statusId === StatusId.SECOND_CHECK_DONE.value
  );
});

const displayedAttachments = computed(() => {
  return props.attachments.slice(
    0,
    calcCountOfDisplayedAttachments(isAttachmentsFolded.value, props.isEditing)
  );
});

return { __sfc: true,company, t, m, props, MIN_FOLDED_ATTACHMENTS_COUNT, isOwnDepartment, allCompanyDepartments, departmentTree, editableDepartmentTree, getCompanyDepartmentTree, hasPermission, P, selectableDepartmentTree, departmentPathByUuid, departmentLabel, selectedProductIndex, isValid, form, modalDeleteProduct, selectedTags, selectableTags, modalAssessedAt, isAttachmentLimitErrorVisible, files, localAttachments, isAttachmentsFolded, productCode, localProductCodes, isUploading, clearProductCode, updateProductCodeList, removeProductCode, closeAssessedAtModal, emits, tags, getCompanyTag, showConfirm, closeDeleteProductModal, handleDeleteProduct, toggleToFoldAttachments, canFoldItems, calcName, attachmentLimit, updateIsValid, updateNote, updateAssessedAt, deleteProduct, noteParts, updateSelectDepartments, assigneeUserOptions, currentSettingsTags, statusChipClass, exportCreateSimple, exportCreateSimpleV3, exportCreateSimpleV3_2, fileInputRef, triggerFileInput, handleFileUpload, removeAttachment, downloadFile, handleChangeAssigneeUsers, calcCountOfDisplayedAttachments, updatableValuesNum, canEdit, isDownloadingJson, downloadJson, canShowCreateSimple, displayedAttachments, StatusId, formatToDate, formatToDateTime, formatDatePickerDay, hasPdfExtension, hasNoPathSeparator, VForm, sharedMessages, SelectTags, TagChip, StateChip, SelectInTextField, TextTruncatedChip, DepartmentTreeSelect }
}

})